import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { ErrorBox, Kbd, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { changedPatterns, draftFrom, issuesFor, withBasePath, type RouteCheck, type RouteDraft, type SiteRoutes } from './routes-draft.ts';
import type { SiteDetail } from './types.ts';
import { ConfirmButton } from './ui.tsx';

const CHECK_DELAY_MS = 250;

/**
 * URL patterns of one site per page type (docs/site-routes.md). Typing checks the whole table on the server
 * (admin.routes.check) and shows the preview; saving (admin.routes.save, audited) keeps every old URL as a 301.
 * Keys: ↑/↓ move between patterns, Esc reverts the focused one, Enter saves.
 */
export function RoutesPanel({ site }: { site: SiteDetail }) {
  const toast = useToast();
  const changes = useChanges();
  const data = useOp<SiteRoutes>('admin.routes.get', { siteRef: site.ref }, changes);
  const routes = data.data;
  const [draft, setDraft] = useState<RouteDraft>({});
  const [check, setCheck] = useState<RouteCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const latestCheck = useRef(0);

  useEffect(() => {
    if (routes) setDraft(draftFrom(routes));
  }, [routes]);

  const pending = routes ? changedPatterns(routes, draft) : {};
  const pendingKey = JSON.stringify(pending);
  const dirty = Object.keys(pending).length > 0;

  useEffect(() => {
    if (!routes || !dirty) {
      setCheck(null);
      return;
    }
    const call = ++latestCheck.current;
    setChecking(true);
    const timer = setTimeout(() => {
      api
        .op<RouteCheck>('admin.routes.check', { siteRef: routes.siteRef, patterns: JSON.parse(pendingKey) as Record<string, string> })
        .then((result) => {
          if (call === latestCheck.current) setCheck(result);
        })
        .catch((error: unknown) => {
          if (call === latestCheck.current) setCheck({ ok: false, issues: [{ pageType: '', message: describeError(error) }], previews: {}, changes: [], shadowed: [] });
        })
        .finally(() => {
          if (call === latestCheck.current) setChecking(false);
        });
    }, CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [routes, dirty, pendingKey]);

  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!routes) return <Loading />;
  if (!routes.routed) return <p className="empty">Embed-only and API-only sites have no pages of their own, so no routes.</p>;

  const disabled = !routes.canEdit;
  const generalIssues = check?.issues.filter((i) => !i.pageType) ?? [];
  const canSave = dirty && !checking && check?.ok === true && !saving;

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    const result = await runWrite<{ changed: { pageType: string }[] }>(toast, 'admin.routes.save', { siteRef: routes.siteRef, patterns: pending }, (r) =>
      r.changed.length === 1 ? 'Route saved; the old URLs redirect' : `${r.changed.length} routes saved; the old URLs redirect`,
    );
    setSaving(false);
    if (result) setCheck(null);
  };

  const onKey = (event: KeyboardEvent<HTMLInputElement>, index: number, type: string, saved: string) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.min(routes.pageTypes.length - 1, Math.max(0, index + (event.key === 'ArrowDown' ? 1 : -1)));
      inputs.current[next]?.focus();
    } else if (event.key === 'Escape' && (draft[type] ?? saved) !== saved) {
      event.preventDefault();
      event.stopPropagation();
      setDraft((d) => ({ ...d, [type]: saved }));
    }
  };

  return (
    <form onSubmit={save} aria-label="Routes">
      <p className="muted small">
        The address of every page type on <strong>{routes.siteName}</strong>, after the base path <code>{routes.basePath}</code>. Placeholders:{' '}
        <code>{'{merchant}'}</code>, <code>{'{category}'}</code>, <code>{'{event}'}</code>. Saved patterns are live within seconds (no deploy); every old address
        answers with a 301 to the new one. Reserved: {routes.reserved.join(' ')}.
      </p>
      {disabled ? <p className="dev-note">Read only: owners and admins of this site change its routes.</p> : null}
      <table className="table compact routes-table" aria-label="URL patterns">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Pattern</th>
            <th scope="col">Example</th>
            <th scope="col" className="actions">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {routes.pageTypes.map((page, index) => {
            const value = draft[page.type] ?? page.pattern;
            const changed = value.trim() !== page.pattern;
            const issue = changed || check ? issuesFor(check, page.type) : null;
            const preview = changed ? (check?.previews[page.type] ?? null) : page.preview;
            const fieldId = `route-${page.type}`;
            return (
              <tr key={page.type} className={issue ? 'row-warn' : undefined}>
                <th scope="row">
                  <label htmlFor={fieldId}>{page.label}</label>
                  <small className="muted block">{page.description}</small>
                </th>
                <td>
                  <div className="route-input">
                    {routes.basePath !== '/' ? <span className="route-base">{routes.basePath}</span> : null}
                    <input
                      id={fieldId}
                      ref={(el) => {
                        inputs.current[index] = el;
                      }}
                      value={value}
                      disabled={disabled}
                      spellCheck={false}
                      autoComplete="off"
                      aria-invalid={issue ? true : undefined}
                      aria-describedby={issue ? `${fieldId}-issue` : undefined}
                      onChange={(e) => setDraft((d) => ({ ...d, [page.type]: e.target.value }))}
                      onKeyDown={(e) => onKey(e, index, page.type, page.pattern)}
                    />
                  </div>
                  {issue ? (
                    <small id={`${fieldId}-issue`} className="field-error block" role="alert">
                      {issue}
                    </small>
                  ) : null}
                  {page.placeholders.length ? <small className="muted block">Needs {page.placeholders.map((p) => `{${p}}`).join(', ')}</small> : null}
                </td>
                <td>
                  {preview ? <code>{preview}</code> : <span className="muted">{checking ? 'Checking…' : '—'}</span>}
                  {changed ? <span className="badge badge-warn">changed</span> : page.custom ? <span className="badge badge-ok">own</span> : <span className="badge badge-none">preset</span>}
                </td>
                <td className="actions">
                  {!disabled && value.trim() !== page.preset ? (
                    <button type="button" className="btn btn-small btn-ghost" title={`Preset: ${withBasePath(routes.basePath, page.preset)}`} onClick={() => setDraft((d) => ({ ...d, [page.type]: page.preset }))}>
                      Use preset
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {generalIssues.map((issue) => (
        <p key={issue.message} className="field-error">
          {issue.message}
        </p>
      ))}

      {check?.ok && check.changes.length ? (
        <div className="dev-note" aria-live="polite">
          <strong>When you save</strong>
          <ul className="plain-list">
            {check.changes.map((change) => (
              <li key={change.pageType}>
                <code>{change.exampleFrom}</code> → <code>{change.exampleTo}</code> (301)
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(check?.shadowed ?? routes.shadowed).map((s) => (
        <p key={s.pageType} className="text-warn small">
          {s.slugs.length} {s.pageType === 'merchant' ? 'merchant' : s.pageType} page{s.slugs.length === 1 ? '' : 's'} not reachable because another route wins ({s.hiddenBy.join(', ')}): {s.slugs.join(', ')}
        </p>
      ))}

      {routes.canEdit ? (
        <div className="form-actions sticky-actions">
          <button type="submit" className="btn btn-primary" disabled={!canSave}>
            Save routes <Kbd>Enter</Kbd>
          </button>
          <button type="button" className="btn btn-ghost" disabled={!dirty} onClick={() => setDraft(draftFrom(routes))}>
            Reset
          </button>
          {checking ? <span className="muted small">Checking…</span> : null}
        </div>
      ) : null}

      <h3 className="section-title">Redirects from previous patterns</h3>
      {routes.previous.length === 0 ? (
        <p className="muted small">None yet. When a pattern changes, its old addresses are kept here and answer with a 301.</p>
      ) : (
        <table className="table compact" aria-label="Redirects">
          <thead>
            <tr>
              <th scope="col">Page</th>
              <th scope="col">Old pattern</th>
              <th scope="col">Example</th>
              <th scope="col">Since</th>
              <th scope="col" className="actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {routes.previous.map((p) => (
              <tr key={p.id}>
                <td>{p.label}</td>
                <td>
                  <code>{withBasePath(routes.basePath, p.pattern)}</code>
                </td>
                <td className="small">
                  <code>{p.exampleFrom}</code> → <code>{p.exampleTo}</code>
                </td>
                <td>{formatDate(p.retiredAt)}</td>
                <td className="actions">
                  {routes.canEdit ? (
                    <ConfirmButton
                      label="Stop redirect"
                      title="Stop this redirect?"
                      confirmLabel="Stop redirect"
                      onConfirm={() => runWrite(toast, 'admin.routes.removeRedirect', { siteRef: routes.siteRef, id: p.id }, 'Redirect removed')}
                    >
                      <p>
                        Addresses like <code>{p.exampleFrom}</code> will answer 404 instead of redirecting. Search engines and old links lose the way to the page.
                      </p>
                    </ConfirmButton>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </form>
  );
}
